import React from 'react'
import {
  format,
  formatDistanceStrict,
  formatDistanceToNow,
  differenceInCalendarDays,
  isDate,
  isValid,
  parse,
} from 'date-fns'
import { da, de, enGB, nb } from 'date-fns/locale'
import { Trans } from '@lingui/macro'

const locales = { en: enGB, da, de, no: nb }

export function parseDate(date) {
  if (date === null) return null
  if (date === 'today') return new Date()
  if (isDate(date)) return date
  return parse(date, 'yyyy-MM-dd', new Date())
}

export function parseDateTime(datetime) {
  if (datetime === null) return null
  if (isDate(datetime)) return datetime
  return parse(datetime, 'yyyy-MM-dd HH:mm:ss', new Date())
}

export function parseDateTimeMilliSeconds(datetime) {
  if (datetime === null) return null
  if (isDate(datetime)) return datetime
  return parse(datetime.substring(0, 19), 'yyyy-MM-dd HH:mm:ss', new Date())
}

export function parseTime(time) {
  if (time === null) return null
  if (isDate(time)) return time
  return parse(time, 'HH:mm', new Date())
}

export function parseTimeMs(time) {
  if (time === null) return null
  if (isDate(time)) return time
  return parse(time, 'HH:mm:ss', new Date())
}

export function formatDateSince(date, timestamp) {
  return differenceInCalendarDays(date, timestamp) <= 0
    ? formatDistanceToNow(timestamp)
    : formatDate(timestamp)
}
export function formatDistance(date, baseDate = new Date(), defaultOptions) {
  const {
    addSuffix = true,
    locale = locales[window.__localeId__],
    unit,
    ...options
  } = defaultOptions || {}
  const parsedDate = parseDateTime(date)

  if (unit === 'hour,minute') {
    const totalMinutes = parseInt(
      formatDistanceStrict(parsedDate, parseDateTime(baseDate), {
        addSuffix,
        locale,
        unit: 'minute',
        ...options,
      }),
      10
    )
    return formatMinutesToHours(totalMinutes)
  } else {
    return formatDistanceStrict(parsedDate, parseDateTime(baseDate), {
      addSuffix,
      locale,
      unit,
      ...options,
    })
  }
}

export function formatMinutesToHours(minutes) {
  const isNegative = Math.sign(minutes) === -1
  const hoursFloat = minutes / 60
  const hours = isNegative ? Math.ceil(hoursFloat) : Math.floor(hoursFloat)
  const minutesMinusHours = minutes - hours * 60
  return (
    <span>
      {hours !== 0 && hours}
      {hours !== 0 && <Trans>h</Trans>} {minutesMinusHours}
      <Trans>min</Trans>
    </span>
  )
}

export function formatMinutesToHoursAndDays(minutes) {
  const isNegative = Math.sign(minutes) === -1
  const absMinutes = Math.abs(minutes)

  const totalHours = Math.floor(absMinutes / 60)
  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  const mins = absMinutes % 60

  return (
    <span>
      {isNegative && '-'}
      {days !== 0 && days}
      {days !== 0 && <Trans>d</Trans>} {hours !== 0 && hours}
      {hours !== 0 && <Trans>h</Trans>} {mins}
      <Trans>min</Trans>
    </span>
  )
}

export function formatDate(date, formatStr = 'P') {
  if (date === null) return ''
  const parsedDate = parseDateTime(date)
  const options = { locale: locales[window.__localeId__] }
  const formattedDate = format(parsedDate, formatStr, options)
  return formattedDate.replace(/\//g, '.')
}

export function formatTime(date, formatStr = 'p') {
  if (date === null) return ''
  const parsedDate = parseDateTime(date)
  const options = { locale: locales[window.__localeId__] }
  return format(parsedDate, formatStr, options)
}

export function formatDateTime(date, formatStr = 'Pp') {
  if (date === null) return ''
  const parsedDate = parseDateTime(date)
  const options = { locale: locales[window.__localeId__] }
  const formattedDate = format(parsedDate, formatStr, options)
  return formattedDate.replace(/\//g, '.')
}

export function formatDateTimeMilliSeconds(date, formatStr = 'P') {
  if (date === null) return ''
  const parsedDate = parseDateTimeMilliSeconds(date)
  const options = { locale: locales[window.__localeId__] }
  const formattedDate = format(parsedDate, formatStr, options)
  return formattedDate.replace(/\//g, '.')
}

export function formatDateTimePicker(date) {
  if (date === null) return ''
  const parsedDate = parseDate(date)
  return format(parsedDate, 'yyyy-MM-dd HH:mm:ss ')
}

export function formatDatePicker(date) {
  if (date === null) return ''
  const parsedDate = parseDate(date)
  if (!isValid(parsedDate) && !!date)
    return format(new Date(date), 'yyyy-MM-dd')
  return format(parsedDate, 'yyyy-MM-dd')
}

function formatStartTime(start_time) {
  if (start_time) {
    return formatTime(start_time)
  } else {
    return <Trans>No record</Trans>
  }
}

function formatEndTime(start_time, end_time, current_time) {
  if (start_time && !end_time) {
    return <Trans>No record</Trans>
  } else if (end_time && formatDate(end_time) !== current_time) {
    return `${formatTime(end_time)} (${formatDate(end_time)})`
  } else if (end_time) {
    return formatTime(end_time)
  } else {
    return null
  }
}

export function formatTimeInterval(start_time, end_time, current_time) {
  const start = formatStartTime(start_time)
  const end = formatEndTime(start_time, end_time, current_time)
  return { start, end }
}
